/* Compact product attributes with an in-place, fixed-height expanded view. */
if (!customElements.get('card-grid-specs')) {
  customElements.define('card-grid-specs', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      // Theme editor updates can connect a custom element before its children
      // have been parsed. Do not mark it initialized until the markup is ready.
      this.initialize();
      if (!this.controller && !this.markupObserver) {
        this.markupObserver = new MutationObserver(() => this.initialize());
        this.markupObserver.observe(this, { childList: true, subtree: true });
      }
    }

    initialize() {
      if (this.controller || !this.isConnected) return;
      this.rows = this.querySelector('.card-grid-specs__rows');
      this.panel = this.querySelector('.card-grid-specs__panel');
      this.buttons = [...this.querySelectorAll('[data-spec-expand]')];
      if (!this.rows || !this.panel || !this.querySelector('[data-spec-panel-value]')) return;
      this.markupObserver?.disconnect();
      this.markupObserver = null;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-spec-expand]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        event.stopPropagation();
        if (button === this.activeButton) this.close();
        else this.open(button);
      }, options);
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.activeButton) {
          event.preventDefault();
          event.stopPropagation();
          this.close();
        }
      }, options);
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.buttons.forEach((button) => {
          this.resizeObserver.observe(button.parentElement.querySelector('.card-grid-specs__value'));
        });
      }
      window.addEventListener('resize', () => this.measure(), options);
      this.measure();
      this.measureFrame = requestAnimationFrame(() => {
        if (this.isConnected) this.measure();
      });
      if (document.fonts) document.fonts.ready.then(() => {
        if (this.isConnected) this.measure();
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.markupObserver?.disconnect();
      this.markupObserver = null;
      cancelAnimationFrame(this.measureFrame);
      this.resizeObserver?.disconnect();
      this.controller = null;
      if (this.activeButton) this.close(false);
      clearTimeout(this.closeTimer);
      if (this.panel) this.panel.hidden = true;
    }

    measure() {
      this.buttons.forEach((button) => {
        const value = button.parentElement.querySelector('.card-grid-specs__value');
        const overflowing = value.scrollWidth > value.clientWidth + 1;
        // The explicit third grid column reserves space even for hidden buttons.
        button.hidden = !overflowing;
        // Keep arrow visibility independent of theme button defaults and stale
        // visibility rules, while preserving the permanent third grid column.
        button.style.setProperty('display', overflowing ? 'inline-flex' : 'none', 'important');
        button.style.setProperty('visibility', overflowing ? 'visible' : 'hidden', 'important');
        button.classList.toggle('is-overflowing', overflowing);
        button.disabled = !overflowing;
        button.setAttribute('aria-hidden', String(!overflowing));
        button.tabIndex = overflowing ? 0 : -1;
      });
      if (this.activeButton) {
        if (this.activeButton.disabled) this.close(false);
        else this.updatePanel();
      }
    }

    remainingText(value) {
      const text = value.textContent;
      const node = value.firstChild;
      if (!node || node.nodeType !== Node.TEXT_NODE) return text;
      const style = getComputedStyle(value);
      const probe = document.createElement('span');
      probe.textContent = '\u2026';
      Object.assign(probe.style, {
        position: 'fixed', visibility: 'hidden', whiteSpace: 'pre',
        font: style.font, letterSpacing: style.letterSpacing,
        fontKerning: style.fontKerning, fontFeatureSettings: style.fontFeatureSettings
      });
      document.body.append(probe);
      const ellipsisWidth = probe.getBoundingClientRect().width;
      value.style.setProperty('--spec-ellipsis-width', `${ellipsisWidth}px`);
      const available = Math.max(0, value.clientWidth - ellipsisWidth);
      probe.remove();
      // Find the visible prefix using the browser's actual font layout. Keep
      // grapheme clusters intact when the truncation falls inside Unicode text.
      const chunks = typeof Intl.Segmenter === 'function'
        ? [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text)].map(item => item.segment)
        : Array.from(text);
      const offsets = [0];
      chunks.forEach(chunk => offsets.push(offsets[offsets.length - 1] + chunk.length));
      const range = document.createRange();
      range.setStart(node, 0);
      let low = 0;
      let high = chunks.length;
      while (low < high) {
        const mid = Math.ceil((low + high) / 2);
        range.setEnd(node, offsets[mid]);
        if (range.getBoundingClientRect().width <= available) low = mid;
        else high = mid - 1;
      }
      return text.slice(offsets[low]).trimStart();
    }

    updatePanel() {
      const row = this.activeButton.parentElement;
      const content = this.querySelector('[data-spec-panel-value]');
      content.textContent = this.remainingText(row.querySelector('.card-grid-specs__value'));
      const top = row.offsetTop + row.offsetHeight;
      this.style.setProperty('--spec-panel-top', `${top}px`);
      // Later rows use a floating continuation rather than moving the active
      // line or increasing the height of the product card.
      this.style.setProperty('--spec-panel-height', `${Math.max(this.clientHeight - top, row.offsetHeight * 2)}px`);
    }

    open(button) {
      if (button.disabled) return;
      if (this.activeButton) this.close(false);
      clearTimeout(this.closeTimer);
      const row = button.parentElement;
      const label = row.querySelector('.card-grid-specs__label').textContent.trim();
      this.activeButton = button;
      button.dataset.expandLabel = button.getAttribute('aria-label');
      button.setAttribute('aria-label', `Collapse ${label}`);
      row.classList.add('is-active');
      let covered = row.nextElementSibling;
      while (covered) {
        covered.classList.add('is-covered');
        covered.inert = true;
        covered.setAttribute('aria-hidden', 'true');
        covered = covered.nextElementSibling;
      }
      this.updatePanel();
      const content = this.querySelector('[data-spec-panel-value]');
      content.scrollTop = 0;
      this.panel.setAttribute('aria-label', label);
      this.panel.setAttribute('aria-hidden', 'false');
      this.panel.inert = false;
      this.panel.hidden = false;
      // Establish the transparent starting state before fading the panel in.
      void this.panel.offsetWidth;
      button.setAttribute('aria-expanded', 'true');
      this.classList.add('is-expanded');
    }

    close(restoreFocus = true) {
      const previous = this.activeButton;
      this.classList.remove('is-expanded');
      this.rows.inert = false;
      this.rows.removeAttribute('aria-hidden');
      this.rows.querySelectorAll('.card-grid-specs__row').forEach(row => {
        row.classList.remove('is-active', 'is-covered');
        row.inert = false;
        row.removeAttribute('aria-hidden');
      });
      previous?.setAttribute('aria-expanded', 'false');
      if (previous?.dataset.expandLabel) previous.setAttribute('aria-label', previous.dataset.expandLabel);
      if (restoreFocus && previous?.isConnected) previous.focus({ preventScroll: true });
      this.panel.inert = true;
      this.panel.setAttribute('aria-hidden', 'true');
      // Keep the panel rendered until its fade-out completes. Reopening cancels
      // this timer so a rapid second click cannot hide the active panel.
      clearTimeout(this.closeTimer);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.panel.hidden = true;
      } else {
        this.closeTimer = setTimeout(() => {
          if (!this.classList.contains('is-expanded')) this.panel.hidden = true;
        }, 540);
      }
      this.activeButton = null;
    }
  });
}
